import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { isEditing } from "@builder.io/sdk-react";

import type { scrollDepthTriggerInputs } from "./ScrollDepthTrigger.builder";

type ScrollDepthTriggerBuilderProps = BuilderComponentProps<typeof scrollDepthTriggerInputs>;

const SCROLL_TRIGGER_ID = "scrollTrigger";

export const ScrollDepthTrigger: FC<ScrollDepthTriggerBuilderProps> = ({
  eventName = "content_reached",
  shouldAvoidMultipleTriggers = false,
}) => {
  const [eventTriggered, setEventTriggered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleCallEvent = useCallback(() => {
    if (eventTriggered) {
      return;
    }
    if (shouldAvoidMultipleTriggers) setEventTriggered(true);

    window.dispatchEvent(new Event(eventName));
  }, [eventTriggered, eventName, shouldAvoidMultipleTriggers]);

  useEffect(() => {
    if (isEditing()) {
      return;
    }

    const handleIntersection = (entries: IntersectionObserverEntry[], _self: IntersectionObserver) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          handleCallEvent();
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 1,
    });
    observer.observe(ref.current!);

    return () => {
      observer.disconnect();
    };
  }, [handleCallEvent]);

  if (isEditing()) {
    return (
      <div id={SCROLL_TRIGGER_ID}>
        Scroll depth trigger component, this is not visible on publish. An event will be triggered when this component
        reaches the viewport.
      </div>
    );
  }

  return <div id={SCROLL_TRIGGER_ID} ref={ref} />;
};
